-- | Module for generating GraphQL queries from structural representations.
module GraphQL.Shape where

import Common

import Data.Argonaut (class DecodeJson, decodeJson)
import Data.Array ((:))
import Data.Symbol (class IsSymbol, reflectSymbol)
import Data.Variant (Variant)
import Data.Variant as Var
import GraphQL.Hacks (Nullish)
import Prim.Row as Row
import Prim.RowList as RL
import Type.Prelude (Proxy(..))


type QueryKey = String

-- | Represents the shape of a GraphQL query.
type QueryShape = Array QueryField

type ShapePath = Array QueryKey

data QueryArg = QueryArg String String

-- | Represents a field in a GraphQL query.
data QueryField
    = QScalar QueryKey
    | QObject QueryKey QueryShape
    | QCall QueryKey (Array QueryArg) QueryShape
    | QAlias QueryKey QueryField
    | QUnion String QueryShape

instance showQF :: Show QueryField where
    show = fieldToString

shapeToString :: QueryShape -> String
shapeToString fields = "{ " <> shapeToStringInner fields <> " }"

-- | `shapeToString` without braces for backward compat with string-based queries
shapeToStringInner :: QueryShape -> String
shapeToStringInner fields = intercalate ", " fieldStrs
  where
    fieldStrs = map fieldToString fields

fieldToString :: QueryField -> String
fieldToString (QScalar key) = key
fieldToString (QObject key fields) = key <> " " <> shapeToString fields
fieldToString (QCall key [] []) = key
fieldToString (QCall key [] fields) = key <> " " <> shapeToString fields
fieldToString (QCall key args []) = key <> "(" <> argsToString args <> ")"
fieldToString (QCall key args fields) = key <> "(" <> argsToString args <> ") " <> shapeToString fields
fieldToString (QUnion vname fields) = "... on " <> vname <> " " <> shapeToString fields
-- Should never happen. In case it does, use the inner alias
fieldToString (QAlias _ f@(QAlias _ _)) = fieldToString f
fieldToString (QAlias name field) = name <> ": " <> fieldToString field

argsToString :: Array QueryArg -> String
argsToString args = intercalate ", " $ map argToString args

argToString :: QueryArg -> String
argToString (QueryArg l r) = l <> ": $" <> r

-- | Class for types whose shape can be determined for use in a GraphQL query
class HasShape :: forall k. k -> Constraint
class HasShape a where
    -- | Return the inner shape of this object.
    -- |
    -- | For scalars this will return an empty list
    shapeOf :: Proxy a -> QueryShape

-- The shape of an array is identical to the inner type's shape
instance arrayShape :: (HasShape a) => HasShape (Array a) where
    shapeOf _ = shapeOf (Proxy :: Proxy a)
else
-- The shape of a `Maybe` is identical to the inner type's shape
instance maybeShape :: (HasShape a) => HasShape (Maybe a) where
    shapeOf _ = shapeOf (Proxy :: Proxy a)
else
instance nullishShape :: (HasShape r) => HasShape (Nullish r) where
    shapeOf _ = shapeOf (Proxy :: Proxy r)
else
-- The shape of a record is determined automatically by `RLHasShape` below
instance recShape :: (RLHasShape row list, RL.RowToList row list) => HasShape (Record row) where
    shapeOf _ = rlShapeOf (Proxy :: Proxy list) (Proxy :: Proxy row)
else
-- The shape of a `Union` corresponds to a GraphQL union translation of its variants
instance unionShape :: (VarHasShape row list, RL.RowToList row list) => HasShape (Union row) where
    shapeOf _ = typename : varShapeOf (Proxy :: Proxy list) (Proxy :: Proxy row)
      where typename = QScalar "__typename"
else
-- All other types are scalar
instance scalarShape :: HasShape a where
    shapeOf _ = []


class RLHasShape (row :: Row Type) (list :: RL.RowList Type) where
    rlShapeOf :: Proxy list -> Proxy row -> QueryShape

instance rlhsCons ::
    ( RLHasShape rtail tail
    , IsSymbol field
    , HasShape value
    , Row.Cons field value rtail row
    , Row.Lacks field rtail
    ) => RLHasShape row (RL.Cons field value tail) where
      rlShapeOf _ _ = thisField : rlShapeOf (Proxy :: Proxy tail) (Proxy :: Proxy rtail)
        where
          thisField = if null shape then QScalar name else QObject name shape
          name = reflectSymbol (Proxy :: Proxy field)
          shape = shapeOf (Proxy :: Proxy value)
instance rlhsNil :: RLHasShape row RL.Nil where
    rlShapeOf _ _ = []


data Union r = Union (Variant r) | UNoMatch

-- | Convert a union into its associated `Variant` or `Nothing` if no tags matched
fromUnion :: forall r. Union r -> Maybe (Variant r)
fromUnion (Union v) = Just v
fromUnion UNoMatch = Nothing

-- | Convert a union into an instance of the given tag if it matches
fromUnionTag :: forall r r' tag val. IsSymbol tag => Row.Cons tag val r' r => Proxy tag -> Union r -> Maybe val
fromUnionTag tag (Union var) = Var.prj tag var
fromUnionTag _ UNoMatch = Nothing

class VarHasShape (row :: Row Type) (list :: RL.RowList Type) where
    varShapeOf :: Proxy list -> Proxy row -> QueryShape

instance varhsCons ::
    ( VarHasShape rtail tail
    , IsSymbol field
    , HasShape value
    , Row.Cons field value rtail row
    , Row.Lacks field rtail
    ) => VarHasShape row (RL.Cons field value tail) where
      varShapeOf _ _ = thisVar : varShapeOf (Proxy :: Proxy tail) (Proxy :: Proxy rtail)
        where
          thisVar = QUnion name shape
          name = reflectSymbol (Proxy :: Proxy field)
          shape = shapeOf (Proxy :: Proxy value)
instance varhsNil :: VarHasShape row RL.Nil where
    varShapeOf _ _ = []


type DecodeFunc ret = Json -> Either JsonDecodeError ret
class UnionDecode (row :: Row Type) (list :: RL.RowList Type) where
    decodeUnion :: Proxy row -> Proxy list -> String -> Maybe (DecodeFunc (Variant row))

instance unDecCons ::
    ( UnionDecode row tail
    , IsSymbol field
    , DecodeJson value
    , Row.Cons field value rtail row
    , Row.Lacks field rtail
    ) => UnionDecode row (RL.Cons field value tail) where
        decodeUnion _ _ tag | tag == (reflectSymbol (Proxy :: Proxy field)) =
            Just (\j -> Var.inj (Proxy :: Proxy field) <$> decodeJson j)
        decodeUnion _ _ tag = decodeUnion (Proxy :: Proxy row) (Proxy :: Proxy tail) tag
instance unDecNil :: UnionDecode row RL.Nil where
    decodeUnion _ _ _ = Nothing

instance unionDecode :: (UnionDecode row list, RL.RowToList row list) => DecodeJson (Union row) where
    decodeJson json = do
       tname :: String <- decodeJObject json >>= (_ .: "__typename")
       let decode = decodeUnion (Proxy :: Proxy row) (Proxy :: Proxy list) tname
       case decode of
           Just d -> Union <$> d json
           Nothing -> Right UNoMatch
